import { SubscriptionCapacities } from '../subscription';
import { isEnscapeRatePlan } from './isEnscapeRatePlan';

export interface RatePlanConfig {
  productRatePlans_Enscape: ProductRatePlansEnscape[];
  productRatePlan_FloatingLicense: ProductRatePlansEnscape;
}

export interface ProductRatePlansEnscape {
  id: string;
  name: string;
}

export interface RatePlanCharge {
  discountAmount?: null | number;
  discountPercentage?: null | number;
  model: string;
  originalChargeId: string;
  quantity: null | number;
  EnterpriseLicenseSeats__c?: null | number;
  specificBillingPeriod: null | number;
  effectiveEndDate: null | string;
}

export interface RatePlan {
  id: string;
  ProductCode__c?: string | null;
  productRatePlanId: string;
  ratePlanCharges: RatePlanCharge[];
  lastChangeType: string;
  quantity?: number | null;
}

export const makeRatePlanCapacities = (ratePlan: RatePlan, config: RatePlanConfig) => {
  const amendments = (
    subscription: Pick<
      SubscriptionCapacities,
      'canAmend' | 'canIncreaseSeats' | 'canDecreaseSeats' | 'canShowUpdateSeatButton'
    >,
  ) => {
    const originalQuantity = ratePlan.quantity || 1;

    const canAmend = () =>
      isEnscapeRatePlan(ratePlan, config) &&
      subscription.canAmend() &&
      !hasSpecificBillingPeriod(ratePlan) &&
      !canCancelEnterpriseCharges();

    const canIncreaseSeats = (currentSeats: number) =>
      canAmend() && subscription.canIncreaseSeats(currentSeats);

    const canDecreaseSeats = (currentSeats: number) =>
      subscription.canDecreaseSeats(currentSeats) && currentSeats > originalQuantity;

    const canShowUpdateSeatButton = (currentSeats?: number) =>
      Boolean(
        currentSeats &&
          currentSeats !== originalQuantity &&
          subscription.canShowUpdateSeatButton(currentSeats),
      );

    return { canAmend, canIncreaseSeats, canDecreaseSeats, canShowUpdateSeatButton };
  };

  const canBeDisplayed = () => isEnscapeRatePlan(ratePlan, config) && !isRemoved(ratePlan);

  const canCancelEnterpriseCharges = () => {
    return ratePlan.ratePlanCharges.some((charge) => {
      return (charge.EnterpriseLicenseSeats__c ?? 0) > 0;
    });
  };

  return {
    amendments,
    canCancelEnterpriseCharges,
    canBeDisplayed,
  };
};

const isRemoved = (ratePlan: RatePlan) => ratePlan.lastChangeType === 'Remove';

/*
  specificBillingPeriod is used for the 3-2-1 campaign
 */
const hasSpecificBillingPeriod = (ratePlan: RatePlan) =>
  ratePlan.ratePlanCharges.some((rtc) => !!rtc.specificBillingPeriod);
